import { SpoilerLevelSelect } from "./SpoilerLevelSelect";
import type { SpoilerLevel } from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import { formatAppVersion } from "@/config/app-version";
import type { TimelineSpan } from "@/data/join-chapters";
import type { JourneyMode } from "@/data/schema/compiled";
import {
  clampTransportSizeLevel,
  TRANSPORT_SIZE_MAX,
  TRANSPORT_SIZE_MIN,
} from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import { CopyLinkButton } from "./CopyLinkButton";
import { TimelineChapterPicker } from "./TimelineChapterPicker";
import { TimelineJourneyToggle } from "./TimelineJourneyToggle";

const version = formatAppVersion(
  import.meta.env.VITE_APP_VERSION,
  import.meta.env.VITE_RELEASE_STAGE,
);

interface TimelineOptionsMenuProps {
  transportSizeLevel: number;
  onTransportSizeLevelChange: (level: number) => void;
  boardModelDebugMenu: boolean;
  boardModelDebug: boolean;
  onBoardModelDebugChange: (enabled: boolean) => void;
  spans: TimelineSpan[];
  spanId: string;
  onSpanIdChange: (id: string) => void;
  universeJourneyView: boolean;
  journey: JourneyMode;
  onJourneyChange: (journey: JourneyMode) => void;
  onJourneyPrefetch: () => void;
  getShareLink: () => string;
  spoilerLevel: SpoilerLevel;
  onSpoilerLevelChange: (level: SpoilerLevel) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TimelineOptionsMenu({
  transportSizeLevel,
  onTransportSizeLevelChange,
  boardModelDebugMenu,
  boardModelDebug,
  onBoardModelDebugChange,
  spans,
  spanId,
  onSpanIdChange,
  universeJourneyView,
  journey,
  onJourneyChange,
  onJourneyPrefetch,
  getShareLink,
  spoilerLevel,
  onSpoilerLevelChange,
  open,
  onOpenChange,
}: TimelineOptionsMenuProps) {
  return (
    <div className="timeline-options" data-open={open}>
      <div className="timeline-options__bar">
        {universeJourneyView ? (
          <TimelineJourneyToggle
            journey={journey}
            onJourneyChange={onJourneyChange}
            onJourneyPrefetch={onJourneyPrefetch}
          />
        ) : null}
        {boardModelDebugMenu ? (
          <button
            type="button"
            className="timeline-options__debug"
            aria-pressed={boardModelDebug}
            onClick={() => onBoardModelDebugChange(!boardModelDebug)}
          >
            Debug
          </button>
        ) : null}
      </div>
      <TimelineChapterPicker spans={spans} spanId={spanId} onSpanIdChange={onSpanIdChange} />
      <button
        type="button"
        className="timeline-options__toggle"
        aria-expanded={open}
        aria-controls="timeline-options-panel"
        onClick={() => onOpenChange(!open)}
      >
        Options
      </button>
      {open ? (
        <div
          id="timeline-options-panel"
          className="timeline-options__panel"
          role="region"
          aria-label="Timeline options"
        >
          <CopyLinkButton getLink={getShareLink} />
          <SpoilerLevelSelect level={spoilerLevel} onChange={onSpoilerLevelChange} />
          <label className="timeline-options__slider-label" htmlFor="transport-size">
            Playback button size
          </label>
          <input
            id="transport-size"
            className="timeline-options__slider"
            type="range"
            min={TRANSPORT_SIZE_MIN}
            max={TRANSPORT_SIZE_MAX}
            step={1}
            value={transportSizeLevel}
            onChange={(event) => {
              onTransportSizeLevelChange(
                clampTransportSizeLevel(Number(event.target.value)),
              );
            }}
          />
          <div className="timeline-options__ticks" aria-hidden="true">
            {Array.from({ length: TRANSPORT_SIZE_MAX }, (_, index) => {
              const level = index + 1;
              return (
                <span
                  key={level}
                  className={
                    level === transportSizeLevel
                      ? "timeline-options__tick timeline-options__tick--active"
                      : "timeline-options__tick"
                  }
                >
                  {level}
                </span>
              );
            })}
          </div>
          {version === null ? null : <p className="timeline-options__version">{version}</p>}
        </div>
      ) : null}
    </div>
  );
}
